class GallerySlider {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.track = container.querySelector('.gallery-slider__track');
    this.items = Array.from(container.querySelectorAll('.gallery-slider__item'));
    this.btnPrev = container.querySelector('.gallery-slider__btn--prev');
    this.btnNext = container.querySelector('.gallery-slider__btn--next');

    this.lightbox = document.getElementById('gallery-lightbox-' + this.sectionId);
    this.lightboxImg = this.lightbox && this.lightbox.querySelector('.gallery-lightbox__img');
    this.lightboxBtnPrev = this.lightbox && this.lightbox.querySelector('.gallery-lightbox__nav--prev');
    this.lightboxBtnNext = this.lightbox && this.lightbox.querySelector('.gallery-lightbox__nav--next');
    this.lightboxClose = this.lightbox && this.lightbox.querySelector('.gallery-lightbox__close');
    this.lightboxBackdrop = this.lightbox && this.lightbox.querySelector('.gallery-lightbox__backdrop');

    this.currentLightboxPos = 0;
    this.autoplayTimer = null;

    // Collect image data: src (hi-res) and alt for each block
    this.imageData = this.items.map(function (item) {
      var btn = item.querySelector('.gallery-slider__image-btn');
      return btn ? { src: btn.dataset.src, alt: btn.dataset.alt || '' } : null;
    });

    // Indices of blocks that actually have an image
    this.validIndices = this.imageData
      .map(function (d, i) { return d ? i : null; })
      .filter(function (i) { return i !== null; });

    this.init();
  }

  init() {
    // 把 lightbox 移到 body 末尾，防止祖先元素的 transform/will-change 干扰 position:fixed
    if (this.lightbox && this.lightbox.parentNode !== document.body) {
      document.body.appendChild(this.lightbox);
    }

    this.bindSlider();
    this.bindLightbox();
    this.initAutoplay();
    this.updateButtons();

    var self = this;
    this.track && this.track.addEventListener('scroll', function () {
      self.updateButtons();
    }, { passive: true });

    // Recalculate button state after layout changes on mobile/desktop
    window.addEventListener('resize', function () { self.updateButtons(); }, { passive: true });
    window.addEventListener('load', function () { self.updateButtons(); });

    // Extra recalculation passes to avoid early mis-detection on desktop layout
    requestAnimationFrame(function () { self.updateButtons(); });
    setTimeout(function () { self.updateButtons(); }, 120);
  }

  /* ── Slider navigation ── */

  bindSlider() {
    var self = this;
    this.btnPrev && this.btnPrev.addEventListener('click', function (e) {
      e.preventDefault();
      self.slide(-1);
    });
    this.btnNext && this.btnNext.addEventListener('click', function (e) {
      e.preventDefault();
      self.slide(1);
    });
  }

  slide(dir) {
    if (!this.track || !this.items.length) return;
    var gap = parseFloat(getComputedStyle(this.track).columnGap) || 16;
    var itemWidth = this.items[0].getBoundingClientRect().width + gap;
    if (!itemWidth || !isFinite(itemWidth)) return;

    var maxScroll = this.track.scrollWidth - this.track.clientWidth;
    var target = this.track.scrollLeft + dir * itemWidth;
    target = Math.max(0, Math.min(maxScroll, target));
    this.track.scrollTo({ left: target, behavior: 'smooth' });
  }

  updateButtons() {
    if (!this.track) return;

    // Mobile: keep arrows clickable; desktop still follows edge disable behavior.
    if (window.matchMedia('(max-width: 749px)').matches) {
      if (this.btnPrev) this.btnPrev.disabled = false;
      if (this.btnNext) this.btnNext.disabled = false;
      return;
    }

    var scrollLeft = this.track.scrollLeft;
    var maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);

    // No overflow on desktop: both buttons disabled.
    if (maxScroll <= 2) {
      if (this.btnPrev) this.btnPrev.disabled = true;
      if (this.btnNext) this.btnNext.disabled = true;
      return;
    }

    if (this.btnPrev) this.btnPrev.disabled = scrollLeft <= 2;
    if (this.btnNext) this.btnNext.disabled = scrollLeft >= maxScroll - 2;
  }

  /* ── Lightbox ── */

  bindLightbox() {
    if (!this.lightbox) return;

    var self = this;

    // Open on image click
    this.items.forEach(function (item, i) {
      var btn = item.querySelector('.gallery-slider__image-btn');
      if (!btn) return;
      btn.addEventListener('click', function () {
        var pos = self.validIndices.indexOf(i);
        if (pos !== -1) self.openLightbox(pos);
      });
    });

    // Close
    this.lightboxClose && this.lightboxClose.addEventListener('click', function () { self.closeLightbox(); });
    this.lightboxBackdrop && this.lightboxBackdrop.addEventListener('click', function () { self.closeLightbox(); });

    // Navigate
    this.lightboxBtnPrev && this.lightboxBtnPrev.addEventListener('click', function () { self.navigateLightbox(-1); });
    this.lightboxBtnNext && this.lightboxBtnNext.addEventListener('click', function () { self.navigateLightbox(1); });

    // Keyboard
    document.addEventListener('keydown', function (e) {
      if (!self.lightbox.classList.contains('is-active')) return;
      if (e.key === 'Escape') self.closeLightbox();
      if (e.key === 'ArrowLeft') self.navigateLightbox(-1);
      if (e.key === 'ArrowRight') self.navigateLightbox(1);
    });

    // Touch swipe in lightbox
    var touchStartX = 0;
    this.lightbox.addEventListener('touchstart', function (e) {
      touchStartX = e.touches[0].clientX;
    }, { passive: true });

    this.lightbox.addEventListener('touchend', function (e) {
      var diff = touchStartX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) self.navigateLightbox(diff > 0 ? 1 : -1);
    });
  }

  openLightbox(validPos) {
    this.currentLightboxPos = validPos;
    this.renderLightboxImage();
    this.lightbox.setAttribute('aria-hidden', 'false');
    this.lightbox.classList.add('is-active');
    document.body.style.overflow = 'hidden';
  }

  closeLightbox() {
    this.lightbox.setAttribute('aria-hidden', 'true');
    this.lightbox.classList.remove('is-active');
    document.body.style.overflow = '';
  }

  navigateLightbox(dir) {
    var len = this.validIndices.length;
    if (!len) return;
    this.currentLightboxPos = (this.currentLightboxPos + dir + len) % len;
    this.renderLightboxImage();
  }

  renderLightboxImage() {
    var dataIndex = this.validIndices[this.currentLightboxPos];
    var data = this.imageData[dataIndex];
    if (!data || !this.lightboxImg) return;
    this.lightboxImg.src = data.src;
    this.lightboxImg.alt = data.alt;
  }

  /* ── Autoplay ── */

  initAutoplay() {
    if (!this.track || this.track.dataset.autoplay !== 'true') return;
    var speed = parseInt(this.track.dataset.speed) || 3000;
    var self = this;

    this.startAutoplay(speed);

    // Pause on hover
    this.container.addEventListener('mouseenter', function () { self.stopAutoplay(); });
    this.container.addEventListener('mouseleave', function () { self.startAutoplay(speed); });
  }

  startAutoplay(speed) {
    this.stopAutoplay();
    var self = this;
    this.autoplayTimer = setInterval(function () {
      var maxScroll = self.track.scrollWidth - self.track.clientWidth;
      if (maxScroll <= 0) return;
      if (self.track.scrollLeft >= maxScroll - 5) {
        self.track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        self.slide(1);
      }
    }, speed);
  }

  stopAutoplay() {
    clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
  }
}

/* ── Init ── */
function initGallerySliders() {
  document.querySelectorAll('.gallery-slider').forEach(function (el) {
    new GallerySlider(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGallerySliders);
} else {
  initGallerySliders();
}
